import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import {
  NoyauPanel,
  type NoyauPanelProps,
} from '@/components/admin/experiences/page-editor/noyau-panel';
import { RichTextField } from '@/components/admin/experiences/page-editor/rich-text-field';
import { Segmented } from '@/components/admin/experiences/page-editor/segmented';
import {
  NotePanel,
  SelectionPanel,
} from '@/components/admin/experiences/page-editor/selection-note-panels';
import { VariableNameField } from '@/components/admin/experiences/page-editor/variable-name-field';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import { READER_WAITING_LABEL } from '@/components/reader';
import { Button } from '@/components/ui/button';
import { ambianceFileUrl } from '@/lib/api';
import type { PaletteContent } from '@/lib/variable-palette';
import { firstPaletteToken, paletteFullSentence } from '@/lib/variable-palette';
import type { VariableZone } from '@/lib/variable-pill';
import type {
  AmbianceAsset,
  ButtonAction,
  ExperienceBlockDraft,
  InputFormat,
  InputLines,
} from '@matter_extractor/shared';
import {
  AMBIANCE_ASSET_RULES,
  EXPERIENCE_BUTTON_LABEL_MAX,
  EXPERIENCE_CAPTION_MAX,
  EXPERIENCE_CHECKBOX_LABEL_MAX,
  EXPERIENCE_INPUT_LABEL_MAX,
  EXPERIENCE_LINK_URL_MAX,
  EXPERIENCE_TEXT_MAX,
  experienceVariableToken,
  linkUrlRefusal,
} from '@matter_extractor/shared';
import { Image as ImageIcon, Play, Volume2 } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useId } from 'react';

/**
 * L'onglet **Contenu** d'un bloc, un panneau par type (refonte modulaire §9.2;
 * planche `Inspecteurs`; issue #58).
 *
 * Eleven types since issue #176 - the Sélection's and the Note's panels are
 * `selection-note-panels.tsx` - and nine since issue #151, the Noyau block's
 * panel being `noyau-panel.tsx`, the one that also writes the experience's
 * reference to the texts. Eight
 * shapes before it, and they share almost nothing: a
 * Texte is a WYSIWYG, an Image is a file and a caption, a Bouton is a label and
 * a choice of action. One file and a switch rather than eight files, because
 * what they DO share is the box, the label style and the hint style of this
 * column - drawn eight times, the first divergence would have been a field
 * 40 px wide in one panel.
 *
 * ## Quatre zones acceptent les variables, une les refuse exprès
 *
 * §9.2 grants them to the Texte block and to the Checkbox's label (« variables
 * acceptées » in as many words), and issue #137 to a Lien's label and to a
 * Texte copiable's text; `experienceTextSitesOf` in `shared` reads exactly
 * those four. A Bouton's label and its waiting label (issue #174, N84), an
 * Input's label and an Image's caption take none: a zone that accepted a token
 * the reader would not resolve is a trap rather than a feature. Every one of the four draws the pill of §8.6 (grill
 * Variables, D5): the Texte through its WYSIWYG, the three plain ones through
 * the painted field the Contexte and the overlay share - a `<textarea>` cannot
 * hold an atom.
 *
 * L'ADRESSE d'un Lien est la seule zone refusée pour la raison inverse : un
 * jeton s'y résoudrait très bien, en une query string qui partirait chez un
 * tiers à chaque clic. Le refus est au schéma (`linkUrlRefusal`), et ce
 * panneau se contente d'en écrire la phrase sous le champ - il ne la
 * reformule pas, et il n'en invente pas une deuxième.
 *
 * ## L'image s'agrandit au survol, en CSS et sans état
 *
 * The plate shows a bigger picture on hovering the thumbnail. A hover that had
 * to be held in React state would re-render the whole inspector on every mouse
 * move across a 64 px square; `group-hover` paints it instead, which is what
 * the rest of this repository does with a hover that reveals something.
 */

const FIELD =
  'w-full rounded-md border-2 border-gray-300 bg-white px-3 read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';
const LABEL = 'block font-semibold text-gray-800 text-xs';
const HINT = 'text-gray-600 text-xs leading-4';

const FORMATS: readonly { value: InputFormat; label: string }[] = [
  { value: 'text', label: 'Texte' },
  { value: 'email', label: 'Email' },
];

const LINES: readonly { value: InputLines; label: string }[] = [
  { value: 'single', label: 'Une ligne' },
  { value: 'multi', label: 'Multi-lignes' },
];

/**
 * « Au clic » of a Bouton (§9.2, H30), and its third segment on the feedback page
 * (retour-experience.md §5, §7.2, maquette `editeur-page-retour.html`, choix 8).
 *
 * « Envoyer le retour » is offered on the feedback page ONLY. There, « Page
 * suivante » stays shown but PALE - choosable, and refused at the save
 * (`feedback-page-exit`): a page that is sent and not left, which the segment
 * says without forbidding the gesture.
 */
const ACTIONS: readonly { value: ButtonAction; label: string }[] = [
  { value: 'next-page', label: 'Page suivante' },
  { value: 'next-temps', label: 'Temps suivant' },
];

const FEEDBACK_ACTIONS: readonly { value: ButtonAction; label: string; dimmed?: boolean }[] = [
  { value: 'next-page', label: 'Page suivante', dimmed: true },
  { value: 'next-temps', label: 'Temps suivant' },
  { value: 'submit', label: 'Envoyer le retour' },
];

export interface ContentPanelProps {
  readonly block: ExperienceBlockDraft;
  readonly readOnly: boolean;
  /** The `@` menu of THIS page, for the letters typed after the `@` (D3). */
  readonly palette: (query: string) => PaletteContent;
  /** What each token IS on this page, for the pill and its fiche (D5, D6). */
  readonly zone: VariableZone;
  /** The name is declared twice or more in the timeline (§8.6). */
  readonly duplicateName: boolean;
  /** « Retenir le choix » only means something with several buttons (§8.6). */
  readonly severalButtons: boolean;
  /** A `next-temps` button of the LAST temps falls back to the page (H30). */
  readonly lastTemps: boolean;
  readonly assets: readonly AmbianceAsset[];
  /**
   * The OTHER Input of the experience already carrying « Nom de la personne
   * dans Sessions » (§9.2, H75), named under the box before the save refuses
   * two (`person-name-repeated`); null when this Input would be the only one.
   */
  readonly personNameHolder: { readonly label: string; readonly where: string } | null;
  /** What the inspector of a Noyau block reads beyond the block (noyau.md §3.2, §3.5, N20). */
  readonly noyau: NoyauInspectorContext;
  /**
   * The block sits on the FEEDBACK page (retour-experience.md §2, #176): its Bouton
   * may « Envoyer le retour », which no other page offers. Absent, a content page.
   */
  readonly feedbackPage?: boolean;
  /** The theme of the page as CSS variables of the reader - the stars of a Note's « Échelle » (§7.2). */
  readonly previewStyle?: CSSProperties | undefined;
  readonly onOpenLibrary: () => void;
  readonly onChange: (next: ExperienceBlockDraft) => void;
}

/**
 * What the inspector of a Noyau block needs that the block does not carry
 * (issue #151): the pin of the EXPERIENCE's reference to the texts, how many
 * blocks share it, the state of the texts, and the pair of example of the
 * preview - with the two gestures that move them.
 */
export type NoyauInspectorContext = Pick<
  NoyauPanelProps,
  'pin' | 'blockCount' | 'detail' | 'pair' | 'onPin' | 'onPair'
>;

export function ContentPanel({
  block,
  readOnly,
  palette,
  zone,
  duplicateName,
  severalButtons,
  lastTemps,
  assets,
  personNameHolder,
  noyau,
  feedbackPage = false,
  previewStyle,
  onOpenLibrary,
  onChange,
}: ContentPanelProps) {
  const labelId = useId();
  const captionId = useId();
  const urlId = useId();
  const urlHintId = useId();
  const waitingId = useId();
  const waitingHintId = useId();

  const asset =
    block.type === 'image' || block.type === 'sound'
      ? (assets.find((one) => one.id === block.assetId) ?? null)
      : null;

  /** The file line the two library blocks share: the name, its facts, the door. */
  function library(kind: 'image' | 'sound') {
    return (
      <div className="space-y-1.5">
        <p className={LABEL}>Fichier</p>
        {asset === null ? (
          <p className={HINT}>
            {block.type !== 'image' && block.type !== 'sound'
              ? null
              : block.assetId === null
                ? 'Aucun fichier choisi : le bloc ne montre rien tant qu’il en manque un.'
                : 'Ce fichier a été supprimé de la médiathèque. Remplace-le : l’enregistrement d’une version est refusé tant qu’il manque.'}
          </p>
        ) : (
          <div className="group relative flex items-center gap-2">
            {kind === 'image' ? (
              <>
                <img
                  src={ambianceFileUrl(asset.id)}
                  alt=""
                  className="size-12 shrink-0 rounded-sm border border-gray-200 object-cover"
                />
                {/* L'aperçu agrandi au survol - en CSS, voir la tête du fichier. */}
                <img
                  src={ambianceFileUrl(asset.id)}
                  alt=""
                  aria-hidden
                  className="pointer-events-none absolute top-14 left-0 z-10 hidden max-h-64 max-w-full rounded-md border-2 border-gray-200 bg-white shadow-lg group-hover:block"
                />
              </>
            ) : (
              <Volume2 aria-hidden className="size-5 shrink-0 text-gray-500" />
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-bleu-nuit text-sm">
                {asset.filename}
              </span>
              <span className="block text-gray-600 text-xs">médiathèque</span>
            </span>
            {kind === 'sound' ? (
              // biome-ignore lint/a11y/useMediaCaption: a mood sound has no speech to caption.
              <audio
                data-testid={PAGE_EDITOR_IDS.assetPlay}
                controls
                preload="none"
                src={ambianceFileUrl(asset.id)}
                className="h-8 max-w-40"
              />
            ) : null}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={PAGE_EDITOR_IDS.assetPick}
          disabled={readOnly}
          onClick={onOpenLibrary}
        >
          {kind === 'image' ? (
            <ImageIcon aria-hidden className="size-4" />
          ) : (
            <Play aria-hidden className="size-4" />
          )}
          Choisir dans la médiathèque
        </Button>
        {kind === 'image' ? (
          <p className={HINT}>
            Formats acceptés : {AMBIANCE_ASSET_RULES.image.formats.join(', ')} ·{' '}
            {AMBIANCE_ASSET_RULES.image.maxBytes / (1024 * 1024)} Mo au plus.
          </p>
        ) : (
          <p className={HINT}>
            Formats acceptés : {AMBIANCE_ASSET_RULES.sound.formats.join(', ')} ·{' '}
            {AMBIANCE_ASSET_RULES.sound.maxBytes / (1024 * 1024)} Mo au plus. En session, le son
            part au premier toucher sur la page.
          </p>
        )}
      </div>
    );
  }

  if (block.type === 'text') {
    return (
      <RichTextField
        value={block.text}
        readOnly={readOnly}
        palette={palette}
        zone={zone}
        onChange={(text) => onChange({ ...block, text })}
      />
    );
  }

  if (block.type === 'image') {
    return (
      <div className="space-y-3">
        {library('image')}
        <div className="space-y-1.5">
          <label htmlFor={captionId} className={LABEL}>
            Légende
          </label>
          <input
            id={captionId}
            value={block.caption}
            readOnly={readOnly}
            maxLength={EXPERIENCE_CAPTION_MAX}
            onChange={(event) => onChange({ ...block, caption: event.currentTarget.value })}
            className={`${FIELD} h-9 text-sm`}
          />
          <p className={HINT}>Facultative, sous l’image, dans la police de texte du thème.</p>
        </div>
        <p className={HINT}>
          La largeur et l’alignement de ce bloc se règlent dans l’onglet Visuel : ce sont ses
          jetons, et un champ montré deux fois serait deux champs à tenir d’accord.
        </p>
      </div>
    );
  }

  if (block.type === 'sound') {
    return (
      <div className="space-y-3">
        {library('sound')}
        <div className="space-y-1.5">
          <p className={LABEL}>Lecture</p>
          <Segmented
            label="Lecture du son"
            size="sm"
            value={block.loop ? 'loop' : 'once'}
            options={[
              { value: 'once', label: 'Une fois' },
              { value: 'loop', label: 'En boucle' },
            ]}
            readOnly={readOnly}
            onChange={(next) => onChange({ ...block, loop: next === 'loop' })}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={labelId} className={LABEL}>
            Volume
          </label>
          <div className="flex items-center gap-2">
            <input
              id={labelId}
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(block.volume * 100)}
              disabled={readOnly}
              onChange={(event) =>
                onChange({ ...block, volume: Number(event.currentTarget.value) / 100 })
              }
              className="h-9 flex-1 accent-bleu-nuit"
            />
            <span className="w-12 shrink-0 text-right text-gray-700 text-sm tabular-nums">
              {Math.round(block.volume * 100)} %
            </span>
          </div>
        </div>
        <p className={HINT}>
          Un son n’a pas d’animation d’entrée : son onglet Animation ne porte que « Apparaît au
          temps ».
        </p>
      </div>
    );
  }

  if (block.type === 'button') {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label htmlFor={labelId} className={LABEL}>
            Libellé
          </label>
          <input
            id={labelId}
            data-testid={PAGE_EDITOR_IDS.blockLabel}
            value={block.label}
            readOnly={readOnly}
            maxLength={EXPERIENCE_BUTTON_LABEL_MAX}
            onChange={(event) => onChange({ ...block, label: event.currentTarget.value })}
            className={`${FIELD} h-9 text-sm`}
          />
        </div>

        <div className="space-y-1.5">
          <p className={LABEL}>Au clic</p>
          {/*
            « Un bouton du dernier temps n'a que la page suivante » (H30) holds on
            a content page; on the feedback page the last temps carries the SEND,
            so the rule does not apply there (maquette, choix 8). A Bouton that
            still says `submit` on a page that is not the feedback page (pasted,
            converted) stays editable whatever its temps: it is the one to repair.
          */}
          <Segmented
            label="Ce que le bouton fait au clic"
            size="sm"
            testId={PAGE_EDITOR_IDS.blockAction}
            value={
              feedbackPage || block.action === 'submit' || !lastTemps ? block.action : 'next-page'
            }
            options={feedbackPage ? FEEDBACK_ACTIONS : ACTIONS}
            readOnly={readOnly || (lastTemps && !feedbackPage && block.action !== 'submit')}
            onChange={(action) => onChange({ ...block, action })}
          />
          <p data-testid={PAGE_EDITOR_IDS.blockActionHint} className={HINT}>
            {block.action === 'submit'
              ? feedbackPage
                ? 'Écrit les réponses de la page, une seule fois, puis les remplace par la phrase de remerciement. La personne reste sur la page.'
                : '« Envoyer le retour » n’existe que sur la page de retour : l’enregistrement d’une version est refusé tant que ce bouton est ailleurs. Choisis une autre action.'
              : feedbackPage
                ? block.action === 'next-page'
                  ? 'Une page de retour ne se quitte pas, elle s’envoie : « Page suivante » est refusé à l’enregistrement. Choisis « Envoyer le retour ».'
                  : 'Le temps suivant part au clic à la place de son délai, et le bouton disparaît une fois le temps joué.'
                : lastTemps
                  ? 'Un bouton du dernier temps n’a que la page suivante (H30).'
                  : block.action === 'next-temps'
                    ? 'Le temps suivant part au clic à la place de son délai, et le bouton disparaît une fois le temps joué.'
                    : 'Le défaut : le bouton est la fin de la page.'}
          </p>
        </div>

        {/* « Libellé d’attente » (noyau.md §4.6, N85; planche `AttenteInspecteurBouton`,
            issue #174): on every Bouton that leads to the next page - « Page
            suivante », or a Bouton of the last temps, which has nothing else
            (H30). A « Temps suivant » one waits for nothing, so the field goes
            and the value written stays in the block, without effect. */}
        {lastTemps || block.action === 'next-page' || block.action === 'submit' ? (
          <div className="space-y-1.5">
            <label htmlFor={waitingId} className={LABEL}>
              Libellé d’attente
            </label>
            <input
              id={waitingId}
              data-testid={PAGE_EDITOR_IDS.blockWaitingLabel}
              value={block.waitingLabel}
              readOnly={readOnly}
              maxLength={EXPERIENCE_BUTTON_LABEL_MAX}
              placeholder={READER_WAITING_LABEL}
              aria-describedby={waitingHintId}
              onChange={(event) => onChange({ ...block, waitingLabel: event.currentTarget.value })}
              className={`${FIELD} h-9 text-sm`}
            />
            <p
              id={waitingHintId}
              data-testid={PAGE_EDITOR_IDS.blockWaitingLabelHint}
              className={HINT}
            >
              {block.action === 'submit'
                ? 'Ce que dit le bouton pendant qu’il attend : le temps d’enregistrer le retour.'
                : 'Ce que dit le bouton pendant qu’il attend : le temps de passer à la page suivante, ou celui de l’analyste si cette page dépend du noyau.'}{' '}
              Vide, il dit <span className="whitespace-nowrap">« {READER_WAITING_LABEL} »</span>.
            </p>
          </div>
        ) : null}

        <label className="flex items-start gap-2 text-gray-800 text-sm">
          <input
            type="checkbox"
            checked={block.rememberChoice}
            disabled={readOnly || !severalButtons}
            onChange={(event) =>
              onChange({ ...block, rememberChoice: event.currentTarget.checked })
            }
            className="mt-0.5 size-4 accent-bleu-nuit"
          />
          <span>
            Retenir le choix
            <span className={`block ${HINT}`}>
              {severalButtons
                ? 'Le libellé pressé s’écrit dans une variable, une seule par page.'
                : 'Un seul bouton sur la page : rien à retenir.'}
            </span>
          </span>
        </label>

        {block.rememberChoice && severalButtons ? (
          <VariableNameField
            value={block.variableName}
            readOnly={readOnly}
            duplicate={duplicateName}
            onChange={(variableName) => onChange({ ...block, variableName })}
          />
        ) : null}
      </div>
    );
  }

  if (block.type === 'input') {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label htmlFor={labelId} className={LABEL}>
            Libellé
          </label>
          <input
            id={labelId}
            data-testid={PAGE_EDITOR_IDS.blockLabel}
            value={block.label}
            readOnly={readOnly}
            maxLength={EXPERIENCE_INPUT_LABEL_MAX}
            onChange={(event) => onChange({ ...block, label: event.currentTarget.value })}
            className={`${FIELD} h-9 text-sm`}
          />
        </div>

        <div className="space-y-1.5">
          <p className={LABEL}>Lignes</p>
          <Segmented
            label="Nombre de lignes"
            size="sm"
            value={block.lines}
            options={LINES}
            readOnly={readOnly}
            /* « Nom de la personne dans Sessions » is an Input of ONE line
               (§9.2, H75): going multi-line takes the flag with it, so no stored
               content ever names a session by a paragraph. */
            onChange={(lines) =>
              onChange({ ...block, lines, personName: lines === 'single' && block.personName })
            }
          />
        </div>

        {block.lines === 'single' ? (
          <div className="space-y-1.5">
            <p className={LABEL}>Format</p>
            <Segmented
              label="Format de la saisie"
              size="sm"
              value={block.format}
              options={FORMATS}
              readOnly={readOnly}
              onChange={(format) => onChange({ ...block, format })}
            />
            <p className={HINT}>Vérifié au navigateur et au serveur, à l’avancée de page.</p>
          </div>
        ) : null}

        <label className="flex items-start gap-2 text-gray-800 text-sm">
          <input
            type="checkbox"
            checked={block.required}
            disabled={readOnly}
            onChange={(event) => onChange({ ...block, required: event.currentTarget.checked })}
            className="mt-0.5 size-4 accent-bleu-nuit"
          />
          <span>
            Obligatoire pour continuer
            <span className={`block ${HINT}`}>
              Tant qu’il est vide, les boutons de la page sont grisés.
            </span>
          </span>
        </label>

        <VariableNameField
          value={block.variableName}
          readOnly={readOnly}
          duplicate={duplicateName}
          onChange={(variableName) => onChange({ ...block, variableName })}
        />

        {block.lines === 'single' ? (
          <label className="flex items-start gap-2 border-gray-200 border-t pt-3 text-gray-800 text-sm">
            <input
              type="checkbox"
              data-testid={PAGE_EDITOR_IDS.personName}
              checked={block.personName}
              disabled={readOnly}
              onChange={(event) => onChange({ ...block, personName: event.currentTarget.checked })}
              className="mt-0.5 size-4 accent-bleu-nuit"
            />
            <span>
              Nom de la personne dans Sessions
              <span className={`block ${HINT}`}>
                Sa valeur nomme la session dans la liste des Sessions. Un seul champ par expérience.
              </span>
              {personNameHolder === null ? null : (
                <span
                  role={block.personName ? 'alert' : undefined}
                  className={`block text-xs leading-4 ${block.personName ? 'text-rouge-deep' : 'text-gray-600'}`}
                >
                  {block.personName
                    ? `« ${personNameHolder.label} », ${personNameHolder.where}, le porte aussi : l’enregistrement refusera deux champs.`
                    : `Déjà porté par « ${personNameHolder.label} », ${personNameHolder.where}.`}
                </span>
              )}
            </span>
          </label>
        ) : null}
      </div>
    );
  }

  if (block.type === 'link') {
    const refusal = block.url === '' ? null : linkUrlRefusal(block.url);
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <p id={labelId} className={LABEL}>
            Libellé
          </p>
          <PaintedTextField
            value={block.label}
            readOnly={readOnly}
            placeholder=""
            ariaLabelledBy={labelId}
            testId={PAGE_EDITOR_IDS.blockLabel}
            insertTestId={null}
            minHeightClass="min-h-16"
            zone={zone}
            renderMenu={(query, insert, fits) => (
              <VariableMenu
                content={palette(query ?? '')}
                query={query}
                onInsert={insert}
                fits={fits}
                full={paletteFullSentence(EXPERIENCE_BUTTON_LABEL_MAX)}
              />
            )}
            firstToken={(query) => firstPaletteToken(palette(query))}
            // The counter is the bound, refused and never cut (#160).
            counter={{ max: EXPERIENCE_BUTTON_LABEL_MAX }}
            onChange={(label) => onChange({ ...block, label })}
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor={urlId} className={LABEL}>
            Adresse
          </label>
          <input
            id={urlId}
            data-testid={PAGE_EDITOR_IDS.blockUrl}
            value={block.url}
            readOnly={readOnly}
            inputMode="url"
            placeholder="https://"
            maxLength={EXPERIENCE_LINK_URL_MAX}
            aria-invalid={refusal !== null}
            aria-describedby={urlHintId}
            onChange={(event) => onChange({ ...block, url: event.currentTarget.value })}
            className={`h-9 w-full rounded-md border-2 bg-white px-3 text-sm read-only:bg-gray-50 read-only:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${
              refusal === null ? 'border-gray-300 focus-visible:border-bleu-nuit' : 'border-rouge'
            }`}
          />
          {refusal === null ? (
            <p id={urlHintId} className={HINT}>
              « https:// » ou « mailto: », et rien d’autre. Aucune variable ici : un jeton dans une
              adresse enverrait une réponse de la personne à un tiers à chaque clic.
            </p>
          ) : (
            <p
              id={urlHintId}
              role="alert"
              data-testid={PAGE_EDITOR_IDS.blockUrlRefusal}
              className="text-rouge-deep text-xs leading-4"
            >
              {refusal} L’enregistrement d’une version sera refusé tant qu’elle est là.
            </p>
          )}
        </div>

        <p className={HINT}>
          Un lien SORT de l’expérience : il ouvre un nouvel onglet, il ne fait pas avancer la page
          et il ne produit aucune variable. Pour passer à la page suivante, c’est un bouton.
        </p>
      </div>
    );
  }

  if (block.type === 'copyable') {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <p id={labelId} className={LABEL}>
            Texte
          </p>
          <PaintedTextField
            value={block.text}
            readOnly={readOnly}
            placeholder=""
            ariaLabelledBy={labelId}
            testId={PAGE_EDITOR_IDS.blockPlainText}
            insertTestId={null}
            minHeightClass="min-h-24"
            zone={zone}
            renderMenu={(query, insert, fits) => (
              <VariableMenu
                content={palette(query ?? '')}
                query={query}
                onInsert={insert}
                fits={fits}
                full={paletteFullSentence(EXPERIENCE_TEXT_MAX)}
              />
            )}
            firstToken={(query) => firstPaletteToken(palette(query))}
            // The counter is the bound, refused and never cut (#160).
            counter={{ max: EXPERIENCE_TEXT_MAX }}
            onChange={(text) => onChange({ ...block, text })}
          />
        </div>

        <p className={HINT}>
          Du texte BRUT, sans gras ni couleur : ce qu’on copie est fait pour être collé ailleurs, où
          les marques ne survivent pas. Les variables, elles, sont résolues avant la copie.
        </p>
        <p className={HINT}>
          Le bouton copie, et rien de plus. Si le navigateur refuse la copie, la personne le lit et
          peut sélectionner le texte à la main.
        </p>
      </div>
    );
  }

  if (block.type === 'noyau') {
    // Its own file (noyau.md §3.2, #151): the only panel that writes the
    // EXPERIENCE (its reference to the texts) beside the block.
    return <NoyauPanel block={block} readOnly={readOnly} onChange={onChange} {...noyau} />;
  }

  // The inspectors of the Sélection and of the Note have their own file (#176, §7.2).
  if (block.type === 'selection') {
    return (
      <SelectionPanel
        block={block}
        readOnly={readOnly}
        palette={palette}
        zone={zone}
        duplicateName={duplicateName}
        onChange={onChange}
      />
    );
  }

  if (block.type === 'note') {
    return (
      <NotePanel
        block={block}
        readOnly={readOnly}
        palette={palette}
        zone={zone}
        duplicateName={duplicateName}
        previewStyle={previewStyle}
        onChange={onChange}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <p id={labelId} className={LABEL}>
          Libellé
        </p>
        {/*
          The painted field of the variable module (D5): the label is the one
          plain text of a page that accepts a token, and a pill is an atom a
          `<textarea>` cannot hold. The menu follows THIS page's position (D3).
        */}
        <PaintedTextField
          value={block.label}
          readOnly={readOnly}
          placeholder=""
          ariaLabelledBy={labelId}
          testId={PAGE_EDITOR_IDS.blockLabel}
          insertTestId={null}
          minHeightClass="min-h-24"
          zone={zone}
          renderMenu={(query, insert, fits) => (
            <VariableMenu
              content={palette(query ?? '')}
              query={query}
              onInsert={insert}
              fits={fits}
              full={paletteFullSentence(EXPERIENCE_CHECKBOX_LABEL_MAX)}
            />
          )}
          firstToken={(query) => firstPaletteToken(palette(query))}
          // The counter is the bound, refused and never cut (#160).
          counter={{ max: EXPERIENCE_CHECKBOX_LABEL_MAX }}
          onChange={(label) => onChange({ ...block, label })}
        />
      </div>

      <label className="flex items-start gap-2 text-gray-800 text-sm">
        <input
          type="checkbox"
          checked={block.required}
          disabled={readOnly}
          onChange={(event) => onChange({ ...block, required: event.currentTarget.checked })}
          className="mt-0.5 size-4 accent-bleu-nuit"
        />
        <span>
          Obligatoire pour continuer
          <span className={`block ${HINT}`}>
            Tant qu’elle n’est pas cochée, les boutons de la page sont grisés.
          </span>
        </span>
      </label>

      <VariableNameField
        value={block.variableName}
        readOnly={readOnly}
        duplicate={duplicateName}
        onChange={(variableName) => onChange({ ...block, variableName })}
      />

      <p className={HINT}>
        Le jeton d’une checkbox est {experienceVariableToken({ kind: 'field', id: block.id })} : il
        porte l’identifiant du bloc, jamais son nom.
      </p>
    </div>
  );
}
